// Phase 6/10 — LiveCook mode pane. README §6.7.3, DECISIONS §E, §F.3.
//
// The pane is a top bar (status + HandednessToggle + LiveCook timer)
// over a single `PhasePane` scoped to the liveCook phase. The PhasePane
// carries the outstanding tabs, the timeline, the concurrent active
// task dials, and the upcoming stack. LiveCook adds:
//   - the damped-red expired overlay once elapsed ≥ nominalDuration;
//   - at expiry, a `PhaseEndPanel` ("LiveCook Time's Up!" + a "LiveCook
//     Complete" checkbox, enabled once every task is checked) — ticking it
//     ends the session and shows the "Enjoy your meal!" screen;
//   - the persistent "Prep done in …" footer chip.

import { useMemo, useState } from 'react';
import type { ClassifiedPlan, ScheduleEntry } from '@cookchow/shared';
import { HandednessToggle, SpeakerToggle, TimerDisplay } from '../design/components';
import { theme } from '../design/theme';
import { useAppStore } from '../state/store';
import { useElapsedSeconds } from './useElapsedSeconds';
import { PhasePane } from './PhasePane';
import { PhaseEndPanel } from './PhaseEndPanel';
import { CookSettingsDrawer } from './CookSettingsDrawer';
import { buildNoteLookup } from './notes';
import { useAssist } from './useAssist';
import { completeTask as completeTaskApi } from '../api/client';
import { correctedNow, formatHHMMSS } from './helpers';

interface LiveCookPaneProps {
  schedule: readonly ScheduleEntry[];
  classified: ClassifiedPlan | null;
  nominalDurationSeconds: number;
  recipe: unknown;
}

export function LiveCookPane({
  schedule,
  classified,
  nominalDurationSeconds,
  recipe,
}: LiveCookPaneProps) {
  const meta = useAppStore((s) => s.meta);
  const liveCookStartedAt = useAppStore((s) => s.liveCookStartedAt);
  const prepFrozenAt = useAppStore((s) => s.prepFrozenAt);
  const planToken = useAppStore((s) => s.planToken);
  const clockOffset = useAppStore((s) => s.clockOffset);
  const completedTaskIds = useAppStore((s) => s.completedTaskIds);
  const markTaskCompleted = useAppStore((s) => s.markTaskCompleted);
  const confirmIndicator = useAppStore((s) => s.confirmIndicator);
  const setClassified = useAppStore((s) => s.setClassified);
  const handedness = useAppStore((s) => s.handedness);
  const setHandedness = useAppStore((s) => s.setHandedness);
  const soundEnabled = useAppStore((s) => s.soundEnabled);
  const setSoundEnabled = useAppStore((s) => s.setSoundEnabled);

  const elapsed = useElapsedSeconds(liveCookStartedAt);
  const expired = elapsed >= nominalDurationSeconds && nominalDurationSeconds > 0;

  const liveCookTasks = useMemo(() => schedule.filter((e) => e.phase === 'liveCook'), [schedule]);
  const noteLookup = useMemo(() => buildNoteLookup(recipe), [recipe]);
  const { matcher: assistMatcher, resolveEntry: resolveAssistEntry } = useAssist();

  const handleCheck = async (taskId: string): Promise<void> => {
    markTaskCompleted(taskId);
    if (!planToken) return;
    try {
      const res = await completeTaskApi({
        planToken,
        taskId,
        completedAt: new Date(correctedNow(clockOffset)).toISOString(),
      });
      setClassified(res.classified, res.nudge, res.serverNow);
    } catch {
      // Calm — offline chip handles the rest.
    }
  };

  // LiveCook Complete: enabled once every liveCook task is checked. Ticking it
  // ends the cook → Cook.tsx renders the "Enjoy your meal!" screen.
  const [completeChecked, setCompleteChecked] = useState(false);
  const allChecked = useMemo(() => {
    const done = new Set(completedTaskIds);
    return liveCookTasks.length > 0 && liveCookTasks.every((t) => done.has(t.id));
  }, [liveCookTasks, completedTaskIds]);

  const handleComplete = (): void => {
    setCompleteChecked(true);
    confirmIndicator(); // sets cookingState 'complete'
  };

  return (
    <main
      aria-label="LiveCook mode cooking screen"
      data-handedness={handedness}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        padding: '1rem',
        background: theme.bg.base,
        color: theme.text.primary,
        minHeight: '100vh',
      }}
    >
      <CookSettingsDrawer />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <span style={{ color: theme.text.primary, fontSize: '1rem', fontWeight: 600 }}>
          {meta?.name ?? 'LiveCook in progress.'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <SpeakerToggle enabled={soundEnabled} onChange={setSoundEnabled} />
          <HandednessToggle value={handedness} onChange={setHandedness} />
          <TimerDisplay
            seconds={elapsed}
            totalSeconds={nominalDurationSeconds}
            expired={expired}
            label="LiveCook"
          />
        </div>
      </div>

      <PhasePane
        tasks={liveCookTasks}
        classified={classified}
        clockOffset={clockOffset}
        handedness={handedness}
        timelineMode="liveCook"
        timelinePhaseChip="LiveCook"
        livecookExpired={expired}
        ariaLabel="LiveCook tasks"
        noteLookup={noteLookup}
        assistMatcher={assistMatcher ?? undefined}
        resolveAssistEntry={resolveAssistEntry}
        onCheck={handleCheck}
        taskAreaOverride={
          expired ? (
            <PhaseEndPanel
              phaseLabel="LiveCook"
              layout="horizontal"
              timesUp
              checked={completeChecked}
              canComplete={allChecked}
              checkboxSide={handedness}
              onToggle={handleComplete}
            />
          ) : undefined
        }
      />

      {prepFrozenAt !== null && (
        <footer
          aria-label="Prep timer footer chip"
          style={{
            marginTop: 'auto',
            display: 'inline-flex',
            alignSelf: 'flex-start',
            background: theme.bg.raised,
            color: theme.text.primary,
            borderRadius: '999px',
            padding: '0.3rem 0.8rem',
            fontSize: '0.85rem',
          }}
        >
          Prep done in {formatHHMMSS(prepFrozenAt)}
        </footer>
      )}
    </main>
  );
}

export default LiveCookPane;
